import type { ChangeRequestId } from "~/change-request/model/change-request";
import type { WorkflowLogOutcome } from "~/workflow-log/model/workflow-log-outcome";

export type NotificationStatus = "unread" | "read" | "archived";

export type JobStatus = "completed" | "failed";

export type SharedResourceType = "system" | "file" | "model" | "artifact";

export type SharedRole = "editor" | "viewer";

export type ReceivedResourceType = "model" | "artifact" | "file";

export type ReceivedResourceEvent = "shared" | "updated" | "revoked";

interface NotificationBase {
  id: string;
  createdAt: Date;
  status: NotificationStatus;
}

interface NotificationActor {
  userId: string | null;
  displayName: string;
}

export interface JobNotification extends NotificationBase {
  type: "job";
  triggeredBy: NotificationActor;
  payload: {
    jobId: string;
    jobStatus: JobStatus;
    systemName?: string;
    functionName: string;
  };
}

export interface ResourceSharedNotification extends NotificationBase {
  type: "share";
  triggeredBy: NotificationActor;
  payload: {
    resourceType: SharedResourceType;
    resourceId: string;
    resourceName: string;
    role: SharedRole;
    shareCount: number;
  };
}

/**
 * Resource received over a Secure Connection. No `triggeredBy`: the sender is known only by connection.
 * Payload is an event-time snapshot; revoked resources are tombstoned and can't be hydrated, and a
 * revoke deliberately carries no name, extension or size.
 */
export interface ReceivedResourceNotification extends NotificationBase {
  type: "received-resource";
  payload: {
    event: ReceivedResourceEvent;
    receivingConnectionId: string;
    receivingConnectionLabel: string;
    resourceId: string;
    resourceType: ReceivedResourceType;
    displayName: string | null;
    /** Always null for `revoked`: the backend sends no file fields with a revoke. */
    fileExtension: string | null;
    fileSize: number | null;
    occurredAt: Date;
  };
}

export interface ChangeRequestNotification extends NotificationBase {
  type: "changeRequest";
  triggeredBy: NotificationActor;
  payload: {
    changeRequestId: ChangeRequestId;
    systemId: string;
    title: string;
  };
}

export interface WorkflowLogCompletedNotification extends NotificationBase {
  type: "workflowLog";
  triggeredBy: NotificationActor;
  payload: {
    workflowLogEntryId: string;
    systemId: string;
    systemName: string | null;
    // The raw status is kept alongside the bucket because it is the text the user recognizes from
    // the workflow log panel, and the bucket loses it.
    status: string;
    outcome: WorkflowLogOutcome;
    title: string;
  };
}

export type Notification =
  | JobNotification
  | ResourceSharedNotification
  | ReceivedResourceNotification
  | ChangeRequestNotification
  | WorkflowLogCompletedNotification;

export function shortId(id: string): string {
  return id.slice(0, 8);
}

export function workflowRunVerb(outcome: WorkflowLogOutcome): string {
  switch (outcome) {
    case "success":
      return "Workflow completed";
    case "failure":
      return "Workflow failed to run";
    case "unspecified":
      return "Workflow finished";
  }
}

export function workflowRunSystemLabel(
  payload: Pick<WorkflowLogCompletedNotification["payload"], "systemId" | "systemName">,
): string {
  return payload.systemName ?? shortId(payload.systemId);
}

export function getNotificationToastTitle(notification: Notification): string {
  switch (notification.type) {
    case "job":
      return notification.payload.jobStatus === "completed" ? "Job completed" : "Job failed";
    case "share":
      return notification.payload.resourceType === "system" ? "System shared with you" : "Resource shared with you";
    case "received-resource":
      return getReceivedResourceToastTitle(notification.payload.event);
    case "changeRequest":
      return `${notification.triggeredBy.displayName} opened change request`;
    case "workflowLog":
      return `${workflowRunVerb(notification.payload.outcome)} on ${workflowRunSystemLabel(notification.payload)}`;
  }
}

export function getNotificationToastDescription(notification: Notification): string {
  switch (notification.type) {
    case "job":
      return notification.payload.functionName;
    case "share":
      return `${notification.triggeredBy.displayName} shared ${notification.payload.resourceName}`;
    case "received-resource":
      return getReceivedResourceMessage(notification);
    case "changeRequest":
      return notification.payload.title;
    case "workflowLog":
      return notification.payload.title;
  }
}

/**
 * The display name is a human label, not a file name, so the extension is never appended to it.
 * A revoke carries no name at all (nothing identifying the revoked file is kept), hence the generic subject.
 */
export function getReceivedResourceDisplayName(payload: ReceivedResourceNotification["payload"]): string {
  if (payload.event === "revoked") return "A resource";
  return payload.displayName ?? "Untitled";
}

export function getReceivedResourceActionParts(event: ReceivedResourceEvent): { before: string; after: string } {
  switch (event) {
    case "shared":
      return { before: "was shared with you via", after: "" };
    case "updated":
      return { before: "was updated via", after: "" };
    case "revoked":
      return { before: "shared via", after: "was revoked by the sender" };
  }
}

export function getReceivedResourceAction(payload: ReceivedResourceNotification["payload"]): string {
  const { before, after } = getReceivedResourceActionParts(payload.event);
  return [before, payload.receivingConnectionLabel, after].filter(Boolean).join(" ");
}

export function getReceivedResourceMessage(notification: ReceivedResourceNotification): string {
  return `${getReceivedResourceDisplayName(notification.payload)} ${getReceivedResourceAction(notification.payload)}`;
}

function getReceivedResourceToastTitle(event: ReceivedResourceEvent): string {
  switch (event) {
    case "shared":
      return "Resource shared with you";
    case "updated":
      return "Resource updated";
    case "revoked":
      return "Resource share revoked";
  }
}
